import { createPortal } from "react-dom";
import { placeBelongsToModule } from "../lib/placeMembership";

interface PlaceFolderLike {
  id: string;
  label: string;
}

interface AddToCollectionModalProps {
  placeId: string;
  allPlaces: any[];
  placeFolders: PlaceFolderLike[];
  onClose: () => void;
  onMove: (folderId: string, thePlace: any, isCurrentCollection: boolean) => void;
}

/**
 * The "Move to Collection" modal, portaled to document.body.
 *
 * Lifted verbatim from PlaceListModule's `addToCollectionPlaceId &&
 * createPortal(...)`. The `addToCollectionPlaceId` render guard stays in the
 * orchestrator. The per-folder membership check (two-clause form) and the
 * place lookup are computed here from props; the actual PATCH + cache
 * invalidation stays in the orchestrator via `onMove`.
 */
export function AddToCollectionModal({
  placeId,
  allPlaces,
  placeFolders,
  onClose,
  onMove,
}: AddToCollectionModalProps) {
  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.3)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "20vh",
        zIndex: 10000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--color-surface)",
          borderRadius: 12,
          padding: 20,
          width: 340,
          maxWidth: "90vw",
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            marginBottom: 4,
            color: "var(--color-text)",
          }}
        >
          Move to Collection
        </div>
        <div
          style={{
            fontSize: 12,
            color: "var(--color-text-faint)",
            marginBottom: 14,
          }}
        >
          {(() => {
            const p = allPlaces.find((pl: any) => pl.id === placeId);
            return p?.title || p?.name || "Place";
          })()}
        </div>
        {placeFolders.length === 0 ? (
          <div
            style={{
              padding: "12px 0",
              fontSize: 13,
              color: "var(--color-text-faint)",
              textAlign: "center",
            }}
          >
            No collections yet. Create one first.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {placeFolders.map((f) => {
              const thePlace = allPlaces.find((pl: any) => pl.id === placeId);
              const isCurrentCollection = thePlace
                ? placeBelongsToModule(thePlace, f.id, {
                    includeModuleLinks: false,
                  })
                : false;
              return (
                <button
                  key={f.id}
                  className={
                    isCurrentCollection
                      ? "place-list-collection-item-current"
                      : ""
                  }
                  onClick={() => onMove(f.id, thePlace, isCurrentCollection)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                    padding: "10px 12px",
                    background: isCurrentCollection ? "#e0f2fe" : "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 14,
                    color: isCurrentCollection ? "var(--color-brand)" : "#334155",
                    borderRadius: 6,
                    textAlign: "left",
                    fontWeight: isCurrentCollection ? 600 : 400,
                  }}
                  onMouseEnter={(e) => {
                    if (!isCurrentCollection)
                      e.currentTarget.style.background = "#f1f5f9";
                  }}
                  onMouseLeave={(e) => {
                    if (!isCurrentCollection)
                      e.currentTarget.style.background = "none";
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                  </svg>
                  {f.label}
                  {isCurrentCollection && (
                    <span style={{ marginLeft: "auto", fontSize: 12 }}>
                      Current
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 14,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "6px 16px",
              borderRadius: 6,
              border: "1px solid #e5e7eb",
              background: "var(--color-surface)",
              fontSize: 13,
              cursor: "pointer",
              color: "var(--color-text-muted)",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
